import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_COLORS } from "config/theme/constants";
import DynamicTable from "components/common/DynamicTable";
import TableHeader from "components/common/TableHeader";
import {
  ltlCustomersString,
  piaggioCustomersString,
  stringConfig,
} from "config/site.string";
import { Breadcrumb, Col, Divider, Drawer, Form, Tabs, Tooltip } from "antd";
import {
  ActivityLogWrapper,
  ActivityWrapper,
  InfoWrapper,
} from "pages/protectedPages/ActivityLog/styles";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store";
import { config } from "config/site.config";
import { isMobile } from "react-device-detect";
// import { loadState } from "library/helpers/sessionStorage";
// import { getGivenPermissions } from "utils/utils";
import {
  EditOutlined,
  HomeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { fetchPiaggioCustomerListRequest } from "redux/piaggioCustomers/actions";
import CustomLoader from "components/common/Loader/CustomLoader";
import { fetchPiaggioItemDetailsListRequest } from "redux/piaggioItemDetails/actions";
import moment from "moment";
import EditPiaggioCustomers from "./EditPiaggioCustomers";
import EditPiaggioItemDetails from "./EditPiaggioItemDetails";
import dayjs from "dayjs";

export const TABLE_PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 10,
  DEFAULT_SORT_FIELD: "id",
  DEFAULT_SORT_ORDER: "DESC",
};

const PiaggioCustomers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  //   const permissionsOfCustomer = loadState("actualPermissionsList");
  //   const { hasViewAccess, hasEditAccess, hasFullAccess }: any =
  //     getGivenPermissions(permissionsOfCustomer, "Piaggio Customers");
  const { piaggioCustomersList, loading, totalCount } = useSelector(
    (state: RootState) => state.piaggioCustomer,
  );
  const { piaggioItemDetailsList, loadingItemDetails, totalCountItemDetails } =
    useSelector((state: RootState) => state.piaggioItemDetails);

  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("1");

  const [pagination, setPagination] = useState({
    current: TABLE_PAGINATION.DEFAULT_PAGE,
    pageSize: TABLE_PAGINATION.DEFAULT_PAGE_SIZE,
    total: 0,
  });

  const [searchValue, setSearchValue] = useState("");
  const [sortField, setSortField] = useState(
    TABLE_PAGINATION.DEFAULT_SORT_FIELD,
  );
  const [sortOrder, setSortOrder] = useState(
    TABLE_PAGINATION.DEFAULT_SORT_ORDER,
  );
  const [piaggioCustomerListData, setPiaggioCustomerListData] =
    useState(piaggioCustomersList);
  const [initialPiaggioCustomerList, setInitialPiaggioCustomerList] =
    useState(piaggioCustomersList);
  const [piaggioItemDetailsListData, setPiaggioItemDetailsListData] = useState(
    piaggioItemDetailsList,
  );
  const [initialPiaggioItemDetailsList, setInitialPiaggioItemDetailsList] =
    useState(piaggioItemDetailsList);
  const [refresh, setRefresh] = useState(false);
  const initialLoadRef = useRef(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState<any>({
    open: false,
    mode: "",
  });
  const [form] = Form.useForm();

  const items = [
    {
      key: "1",
      label: "Address Details",
      children: <></>,
    },
    {
      key: "2",
      label: "Item Details",
      children: <></>,
    },
  ];

  useEffect(() => {
    const newTotal = activeTab === "1" ? totalCount : totalCountItemDetails;
    setPagination((prev) => ({
      ...prev,
      total: newTotal,
    }));
  }, [activeTab, totalCount, totalCountItemDetails]);

  useEffect(() => {
    if (initialLoadRef.current) {
      setIsInitialized(true);
      const defaultParams = {
        pageNumber: pagination.current,
        pageSize: pagination.pageSize,
        sortColumn: sortField,
        sortOrder,
        searchValue,
      };

      if (activeTab === "1") {
        dispatch(
          fetchPiaggioCustomerListRequest(
            config.getPiaggioCustomersList,
            defaultParams,
          ),
        );
      } else if (activeTab === "2") {
        dispatch(
          fetchPiaggioItemDetailsListRequest(
            config.getPiaggioItemDetailsList,
            defaultParams,
          ),
        );
      }
      initialLoadRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (isInitialized) {
      const defaultParams = {
        pageNumber: pagination.current,
        pageSize: pagination.pageSize,
        sortColumn: sortField,
        sortOrder,
        searchValue,
      };

      if (activeTab === "1") {
        dispatch(
          fetchPiaggioCustomerListRequest(
            config.getPiaggioCustomersList,
            defaultParams,
          ),
        );
      } else if (activeTab === "2") {
        dispatch(
          fetchPiaggioItemDetailsListRequest(
            config.getPiaggioItemDetailsList,
            defaultParams,
          ),
        );
      }
    }
  }, [
    refresh,
    activeTab,
    pagination.current,
    pagination.pageSize,
    sortField,
    sortOrder,
    searchValue,
    dispatch,
  ]);

  // Update customer list data when redux state changes
  useEffect(() => {
    setPiaggioCustomerListData(piaggioCustomersList);
    setInitialPiaggioCustomerList(piaggioCustomersList);
  }, [piaggioCustomersList]);

  // Update item details data when redux state changes
  useEffect(() => {
    setPiaggioItemDetailsListData(piaggioItemDetailsList);
    setInitialPiaggioItemDetailsList(piaggioItemDetailsList);
  }, [piaggioItemDetailsList]);

  const handleTableChange = (
    newPagination: any,
    _filters: any,
    sorter: any,
  ) => {
    const { current, pageSize } = newPagination;
    const newSortField = sorter.field || TABLE_PAGINATION.DEFAULT_SORT_FIELD;
    const newSortOrder = sorter.order || TABLE_PAGINATION.DEFAULT_SORT_ORDER;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));
    setSortField(newSortField);
    setSortOrder(newSortOrder);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPagination((prev) => ({
      ...prev,
      current: TABLE_PAGINATION.DEFAULT_PAGE,
    }));
  };

  const handleEdit = (record: any) => {
    setRefresh(false);
    if (activeTab === "1") {
      const parseDate = (value?: string) => {
        if (!value) return null;
        const parsed = dayjs.utc(value).isValid() ? dayjs.utc(value) : null;
        console.log("parsed", typeof parsed);
        return parsed?.isValid() ? parsed : null;
      };
      const normalizedRecord = record
        ? {
            ...record,
            pickupDateTime: parseDate(record.pickupDateTime),
            pickupCloseDateTime: parseDate(record.pickupCloseDateTime),
            scheduledDeliveryDateTime: parseDate(
              record.scheduledDeliveryDateTime,
            ),
          }
        : null;
      setSelectedRecord(normalizedRecord);
      setIsModalOpen({
        open: true,
        mode: "EDIT",
      });
      form.setFieldsValue(normalizedRecord);
    } else {
      setSelectedRecord(record);
      setIsModalOpen({
        open: true,
        mode: "EDIT",
      });
      form.setFieldsValue(record);
    }
  };

  const openAddCustomer = (from?: string) => {
    setIsModalOpen({
      open: true,
      mode: from,
    });
  };

  const handleOk = () => {
    setIsModalOpen({
      open: false,
      mode: "",
    });
  };

  const handleCancel = () => {
    setIsModalOpen({
      open: false,
      mode: "",
    });
  };

  // Handle tab change
  const handleTabChange = (newActiveTab: string) => {
    console.log("Tab changing from", activeTab, "to", newActiveTab);
    setActiveTab(newActiveTab);
    // Reset pagination to first page
    setPagination((prev) => ({
      ...prev,
      current: TABLE_PAGINATION.DEFAULT_PAGE,
    }));
    // Clear search
    setSearchValue("");
    // Reset sorting
    setSortField(TABLE_PAGINATION.DEFAULT_SORT_FIELD);
    setSortOrder(TABLE_PAGINATION.DEFAULT_SORT_ORDER);
  };

  const columnsDataForCustomers = [
    {
      title: "Consignee Reference",
      dataIndex: "consigneeReference",
      isSorter: true,
      width: "11%",
    },
    {
      title: "Consignee Name",
      dataIndex: "consigneeName",
      isSorter: true,
      width: "11%",
    },
    {
      title: "Consignee Phone",
      dataIndex: "consigneePhone",
      isSorter: true,
      width: "11%",
      render: (phone: any) => phone || "",
    },
    {
      title: "Consignee Address",
      dataIndex: "consigneeAddress1",
      isSorter: true,
      width: "11%",
      render: (address1: string, record: any) => {
        const fullAddress = [
          address1,
          record.consigneeAddress2,
          record.consigneeCity,
          record.consigneeState,
          record.consigneeZip,
        ]
          .filter(Boolean)
          .join(", ");

        const truncatedAddress =
          fullAddress?.length > 50
            ? `${fullAddress.substring(0, 50)}...`
            : fullAddress;

        return (
          <Tooltip title={fullAddress}>
            <span style={{ cursor: "default" }}>{truncatedAddress}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Shipper Name",
      dataIndex: "shipperName",
      isSorter: true,
      width: "9%",
    },
    {
      title: "Shipper Address",
      dataIndex: "shipperAddress1",
      isSorter: true,
      width: "11%",
      render: (address1: string, record: any) => {
        const fullAddress = [
          address1,
          record.shipperAddress2,
          record.shipperCity,
          record.shipperState,
          record.shipperZip,
        ]
          .filter(Boolean)
          .join(", ");

        const truncatedAddress =
          fullAddress?.length > 50
            ? `${fullAddress.substring(0, 50)}...`
            : fullAddress;

        return (
          <Tooltip title={fullAddress}>
            <span style={{ cursor: "default" }}>{truncatedAddress}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Pickup Date Time",
      dataIndex: "pickupDateTime",
      isSorter: true,
      width: "10%",
      render: (text: any) => {
        return text ? moment(text).format("DD-MMM-YYYY HH:mm:ss") : "";
      },
    },
    {
      title: "Pickup Close Date Time",
      dataIndex: "pickupCloseDateTime",
      isSorter: true,
      width: "10%",
      render: (text: any) => {
        return text ? moment(text).format("DD-MMM-YYYY HH:mm:ss") : "";
      },
    },
    {
      title: "Scheduled Delivery Date Time",
      dataIndex: "scheduledDeliveryDateTime",
      isSorter: true,
      width: "10%",
      render: (text: any) => {
        return text ? moment(text).format("DD-MMM-YYYY HH:mm:ss") : "";
      },
    },
    {
      title: "Hazmat",
      dataIndex: "isHazmat",
      isSorter: true,
      width: "9%",
      render: (isHazmat: number) => {
        return isHazmat ? "Yes" : "No";
      },
    },
    {
      title: "Special Instructions",
      dataIndex: "specialInstructions",
      isSorter: true,
      width: "12%",
      ellipsis: true,
      render: (text: string) =>
        text && text.length > 200 ? `${text.substring(0, 200)}...` : text || "",
    },
  ];

  const columnsDataForItemDetails = [
    {
      title: "Serial Number/Material Name",
      dataIndex: "serialNumber",
      isSorter: true,
      width: "20%",
      render: (serialNumber: string) => {
        return serialNumber || "";
      },
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      isSorter: true,
      width: "8%",
      render: (quantity: number) => quantity || 0,
    },
    {
      title: "Container Type",
      dataIndex: "containerType",
      isSorter: true,
      width: "10%",
      render: (type: string) => type || "",
    },
    {
      title: "Description",
      dataIndex: "description",
      isSorter: true,
      width: "18%",
      ellipsis: true,
      render: (description: string) => {
        return description && description.length > 60
          ? `${description.substring(0, 60)}...`
          : description || "";
      },
    },
    {
      title: "Weight (lbs)",
      dataIndex: "weight",
      isSorter: true,
      width: "8%",
      render: (weight: number) => (weight ? `${weight}` : ""),
    },
    {
      title: "Length (in)",
      dataIndex: "length",
      isSorter: true,
      width: "8%",
      render: (length: number) => (length ? `${length}` : ""),
    },
    {
      title: "Width (in)",
      dataIndex: "width",
      isSorter: true,
      width: "8%",
      render: (width: number) => (width ? `${width}` : ""),
    },
    {
      title: "Height (in)",
      dataIndex: "height",
      isSorter: true,
      width: "8%",
      render: (height: number) => (height ? `${height}` : ""),
    },
  ];

  const popOverIconStyle = {
    color: BASE_COLORS.curiousBlue,
    marginRight: 5,
  };

  const columnAction = {
    title: stringConfig.actions,
    dataIndex: "",
    key: "x",
    width: "7%",

    render: (item: any) => (
      <EditOutlined
        style={{ cursor: "pointer", ...popOverIconStyle }}
        onClick={() => handleEdit(item)}
      />
    ),
  };

  const columnsData = useMemo(
    () =>
      activeTab === "1" ? columnsDataForCustomers : columnsDataForItemDetails,
    [activeTab],
  );

  const currentData =
    activeTab === "1" ? piaggioCustomerListData : piaggioItemDetailsListData;
  const currentInitialData =
    activeTab === "1"
      ? initialPiaggioCustomerList
      : initialPiaggioItemDetailsList;
  const currentLoading = activeTab === "1" ? loading : loadingItemDetails;
  const currentHeaderText =
    activeTab === "1"
      ? piaggioCustomersString.customerLog
      : piaggioCustomersString.itemDetailsLog;

  return (
    <ActivityWrapper>
      <InfoWrapper>
        <Breadcrumb
          items={[
            {
              title: <HomeOutlined />,
            },
            {
              title: <span style={{ cursor: "pointer" }}>Settings</span>,
              onClick: () => navigate("/settings"),
            },
            {
              title: <span>Piaggio Addresses</span>,
            },
          ]}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Tooltip
            title="View and manage piaggio addresses and item details information. This page provides an interface for adding and updating address details and item details for Piaggio customer."
            placement="bottomRight"
            overlayInnerStyle={{ maxWidth: 300 }}
          >
            <InfoCircleOutlined style={{ cursor: "pointer" }} />
          </Tooltip>
        </div>
      </InfoWrapper>
      <Divider style={{ marginBottom: "10px", marginTop: "10px" }} />
      {activeTab === "1" ? (
        <ActivityLogWrapper>
          {currentLoading && <CustomLoader />}
          <div style={{ paddingLeft: 10 }}>
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={items}
            />
            <Col span={24} style={{ marginTop: 15 }} className="tableRow">
              <TableHeader
                showModal={() => openAddCustomer("ADD")}
                placeHolder={stringConfig.searchPlaceholder}
                buttonText={ltlCustomersString.addAddress}
                inputWidth={isMobile ? "100%" : 180}
                leftHeaderText={currentHeaderText}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onServerSearch={handleSearch}
                searchLoading={currentLoading}
                initialData={currentInitialData}
                columnsKey={columnsData.map(({ dataIndex }) => dataIndex)}
                // isShowButton={!hasViewAccess}
              />
              <DynamicTable
                columns={columnsData}
                columnActions={columnAction}
                data={currentData}
                loading={currentLoading}
                onChange={handleTableChange}
                isPagination={true}
                pagination={pagination}
                sortField={sortField}
                sortOrder={sortOrder}
                totalCounts={pagination.total}
                initialData={currentInitialData}
                scrollXWidth={1350}
              />
              {isModalOpen.open && (
                <Drawer
                  title={
                    isModalOpen.mode === "ADD"
                      ? piaggioCustomersString.customerDetailsLog
                      : piaggioCustomersString.customerDetailsEditLog
                  }
                  onClose={() => handleCancel()}
                  open={isModalOpen.open}
                  className="add-user-drawer"
                  destroyOnClose
                  width={900}
                >
                  <EditPiaggioCustomers
                    form={form}
                    handleOk={handleOk}
                    isEdit={isModalOpen.mode === "EDIT"}
                    handleCancel={handleCancel}
                    selectedRecord={selectedRecord}
                    onClose={(isSuccess) => {
                      if (isSuccess) {
                        setSearchValue("");
                        setPagination((prev) => ({
                          ...prev,
                          current: TABLE_PAGINATION.DEFAULT_PAGE,
                        }));
                        setRefresh(true);
                      }
                      setSelectedRecord(null);
                      form.resetFields();
                      setIsModalOpen({
                        open: false,
                        mode: "",
                      });
                    }}
                  />
                </Drawer>
              )}
            </Col>
          </div>
        </ActivityLogWrapper>
      ) : (
        <ActivityLogWrapper>
          {currentLoading && <CustomLoader />}
          <div style={{ paddingLeft: 10 }}>
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={items}
            />
            <Col span={24} style={{ marginTop: 15 }} className="tableRow">
              <TableHeader
                showModal={() => openAddCustomer("ADD")}
                placeHolder={stringConfig.searchPlaceholder}
                buttonText={piaggioCustomersString.addItem}
                inputWidth={isMobile ? "100%" : 180}
                leftHeaderText={currentHeaderText}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onServerSearch={handleSearch}
                searchLoading={currentLoading}
                initialData={currentInitialData}
                columnsKey={columnsData.map(({ dataIndex }) => dataIndex)}
                // isShowButton={!hasViewAccess}
              />
              <DynamicTable
                columns={columnsData}
                columnActions={columnAction}
                data={currentData}
                loading={currentLoading}
                onChange={handleTableChange}
                isPagination={true}
                pagination={pagination}
                sortField={sortField}
                sortOrder={sortOrder}
                totalCounts={pagination.total}
                initialData={currentInitialData}
                scrollXWidth={1350}
              />
              {isModalOpen.open && (
                <Drawer
                  title={
                    isModalOpen.mode === "ADD"
                      ? piaggioCustomersString.itemDetailsAddLog
                      : piaggioCustomersString.itemDetailsEditLog
                  }
                  onClose={() => handleCancel()}
                  open={isModalOpen.open}
                  className="add-user-drawer"
                  destroyOnClose
                  width={500}
                >
                  <EditPiaggioItemDetails
                    form={form}
                    handleOk={handleOk}
                    isEdit={isModalOpen.mode === "EDIT"}
                    handleCancel={handleCancel}
                    selectedRecord={selectedRecord}
                    onClose={(isSuccess) => {
                      if (isSuccess) {
                        setSearchValue("");
                        setPagination((prev) => ({
                          ...prev,
                          current: TABLE_PAGINATION.DEFAULT_PAGE,
                        }));
                        setRefresh(true);
                      }
                      setSelectedRecord(null);
                      form.resetFields();
                      setIsModalOpen({
                        open: false,
                        mode: "",
                      });
                    }}
                  />
                </Drawer>
              )}
            </Col>
          </div>
        </ActivityLogWrapper>
      )}
    </ActivityWrapper>
  );
};

export default PiaggioCustomers;
